<!-- Hero Slider -->
<section class="relative h-[500px] flex items-center justify-center overflow-hidden" *ngIf="bannerItems.length > 0; else bannerSkeleton">
    <!-- Slides -->
    <div *ngFor="let banner of bannerItems; let i = index"
         class="absolute inset-0 transition-opacity duration-700 ease-in-out"
         [class.opacity-100]="i === currentSlide"
         [class.opacity-0]="i !== currentSlide"
         [class.z-10]="i === currentSlide"
         [class.z-0]="i !== currentSlide">
        <img
            class="absolute inset-0 w-full h-full object-cover"
            [src]="banner.asset | assetPreview:1920:500"
            [alt]="banner.title"
        />
        <div class="absolute inset-0 bg-black/40"></div>
        <div class="relative h-full flex items-center w-full max-w-[1472px] mx-auto px-6 xl:px-[224px]">
            <div class="max-w-[635px] text-white">
                <h2 *ngIf="banner.title" class="text-[40px] leading-[48px] font-semibold mb-2">
                    {{ banner.title }}
                </h2>
                <p *ngIf="banner.description" class="text-[20px] leading-[28px] mb-6">
                    {{ banner.description }}
                </p>
                <a *ngIf="banner.buttonLabel && banner.buttonLink"
                   [href]="banner.buttonLink"
                   class="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-primary/90 transition-colors">
                    {{ banner.buttonLabel }}
                </a>
            </div>
        </div>
    </div>

    <!-- Navigation Arrows -->
    <ng-container *ngIf="bannerItems.length > 1">
        <button (click)="prevSlide()"
                class="absolute left-4 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors">
            <fa-icon icon="chevron-left"></fa-icon>
        </button>
        <button (click)="nextSlide()"
                class="absolute right-4 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors">
            <fa-icon icon="chevron-right"></fa-icon>
        </button>

        <!-- Dots -->
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            <button *ngFor="let banner of bannerItems; let i = index"
                    (click)="goToSlide(i)"
                    class="w-3 h-3 rounded-full transition-colors"
                    [ngClass]="i === currentSlide ? 'bg-white' : 'bg-white/50'">
            </button>
        </div>
    </ng-container>
</section>

<!-- Banner Skeleton -->
<ng-template #bannerSkeleton>
    <section class="relative h-[500px] flex items-center justify-center overflow-hidden bg-gray-200 animate-pulse">
        <div class="relative w-full max-w-[1472px] mx-auto px-6 xl:px-[224px]">
            <div class="max-w-[635px]">
                <div class="h-12 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div class="h-6 bg-gray-300 rounded w-1/2 mb-6"></div>
                <div class="h-12 bg-gray-300 rounded w-40"></div>
            </div>
        </div>
    </section>
</ng-template>

<!-- Categories Grid -->
<section class="bg-white py-20 px-6 xl:px-[224px]">
    <div class="max-w-[1472px] mx-auto flex flex-col gap-4">
        <ng-container *ngIf="collections$ | async as collections; else categoriesSkeleton">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <a *ngFor="let collection of collections"
                   [routerLink]="['/category', collection.slug]"
                   class="bg-surface flex items-center gap-4 h-24 px-6 rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                    <img src="assets/collection-image.png" alt="" class="w-10 h-10 shrink-0 object-contain">
                    <span class="text-heading text-lg font-semibold leading-6">{{ collection.name }}</span>
                </a>
            </div>
        </ng-container>
        <ng-template #categoriesSkeleton>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div *ngFor="let i of [1,2,3,4,5,6,7,8,9]"
                     class="bg-surface flex items-center gap-4 h-24 px-6 rounded-lg animate-pulse">
                    <div class="w-10 h-10 shrink-0 bg-gray-300 rounded-full"></div>
                    <div class="h-5 bg-gray-300 rounded w-3/4"></div>
                </div>
            </div>
        </ng-template>
    </div>
</section>

<!-- Special Offers -->
<ng-container *ngIf="specialOffers$ | async as products">
    <section id="special-offers" class="bg-white py-20 px-6 xl:px-[224px]" *ngIf="products.length > 0">
        <div class="max-w-[1472px] mx-auto flex flex-col gap-6">
            <h2 class="text-heading text-[32px] leading-[40px] font-semibold">{{ 'home.specialOffers' | translate }}</h2>
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <vsf-product-card *ngFor="let product of products; trackBy: trackByProductId"
                                  [product]="product">
                </vsf-product-card>
            </div>
        </div>
    </section>
</ng-container>

<!-- Best Sellers -->
<ng-container *ngIf="bestSellers$ | async as products">
    <section id="best-sellers" class="bg-white py-20 px-6 xl:px-[224px]" *ngIf="products.length > 0">
        <div class="max-w-[1472px] mx-auto flex flex-col gap-6">
            <h2 class="text-heading text-[32px] leading-[40px] font-semibold">{{ 'home.bestSellers' | translate }}</h2>
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <vsf-product-card *ngFor="let product of products; trackBy: trackByProductId"
                                  [product]="product">
                </vsf-product-card>
            </div>
        </div>
    </section>
</ng-container>

<!-- Brands -->
<ng-container *ngIf="brandItems$ | async as brands">
    <section id="brands" class="bg-surface py-20 px-6 xl:px-[224px]" *ngIf="brands.length > 0">
        <div class="max-w-[1472px] mx-auto flex flex-col gap-6">
            <div class="flex items-center justify-between">
                <h2 class="text-heading text-[32px] leading-[40px] font-semibold">{{ 'home.brands' | translate }}</h2>
                <a routerLink="/brands"
                   class="text-primary font-semibold text-base hover:underline transition-colors">{{ 'home.seeMore' | translate }}</a>
            </div>
            <div class="flex flex-wrap items-center justify-center gap-8">
                <ng-container *ngFor="let brand of brands | slice:0:4">
                    <a *ngIf="brand.link; else noLink"
                       [href]="brand.link"
                       target="_blank"
                       class="shrink-0">
                        <img [src]="brand.asset?.preview"
                             [alt]="brand.name || 'Brand'"
                             class="h-[60px] w-auto object-contain opacity-80 hover:opacity-100 transition-opacity">
                    </a>
                    <ng-template #noLink>
                        <div class="shrink-0">
                            <img [src]="brand.asset?.preview"
                                 [alt]="brand.name || 'Brand'"
                                 class="h-[60px] w-auto object-contain opacity-80 hover:opacity-100 transition-opacity">
                        </div>
                    </ng-template>
                </ng-container>
            </div>
        </div>
    </section>
</ng-container>

<!-- New Products -->
<section id="new-products" class="bg-white py-20 px-6 xl:px-[224px]">
    <div class="max-w-[1472px] mx-auto flex flex-col gap-6">
        <h2 class="text-heading text-[32px] leading-[40px] font-semibold">{{ 'home.newProducts' | translate }}</h2>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4" *ngIf="newProducts$ | async as products; else newProductsSkeleton">
            <vsf-product-card *ngFor="let product of products; trackBy: trackByProductId"
                              [product]="product">
            </vsf-product-card>
        </div>
        <ng-template #newProductsSkeleton>
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div *ngFor="let i of [1,2,3,4]" class="flex flex-col gap-2 animate-pulse">
                    <div class="bg-gray-200 rounded h-[350px] lg:h-[482px]"></div>
                    <div class="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div class="h-5 bg-gray-200 rounded w-2/3"></div>
                    <div class="h-5 bg-gray-200 rounded w-1/4"></div>
                </div>
            </div>
        </ng-template>
    </div>
</section>
